import createItemIdForRequest from './createItemIdForRequest';
import getDataUriCount from './getDataUriCount';
import getRecipientsFromWellViewState from './getRecipientsFromWellViewState';
import removeSrcAttributeFromInlineImages from './removeSrcAttributeFromInlineImages';
import setFromInfoToMessage from './setFromInfoToMessage';
import setFromInfoToPost from './setFromInfoToPost';
import tryUpdateExtendedPropertiesForAppendOnSend from './tryUpdateExtendedPropertiesForAppendOnSend';
import tryUpdateExtendedPropertiesForInternetHeaders from './tryUpdateExtendedPropertiesForInternetHeaders';
import { isGroupScenario } from './viewStateUtils';
import { preHandleErrorResponse } from './wasclUtils';
import clearKeysOfInternetHeadersToBeRemoved from '../actions/clearKeysOfInternetHeadersToBeRemoved';
import updateComposeItemId from '../actions/updateComposeItemId';
import updateInternetMessageId from '../actions/updateInternetMessageId';
import { findComposePlugins } from '../plugins/ComposePlugins';
import ComposeViewState, { ComposeOperation } from '../store/schema/ComposeViewState';
import GroupComposeViewState from '../store/schema/GroupComposeViewState';
import PublicFolderComposeViewState from '../store/schema/PublicFolderComposeViewState';
import TextDirection from '../store/schema/TextDirection';
import createAppendOnSendBlock from '../utils/createAppendOnSendBlock';
import { MentionPluginDataProviderInterface } from 'owa-editor-mentions-picker-plugin/lib/providers/MentionPluginDataProvider';
import { isFeatureEnabled } from 'owa-feature-flags';
import { ARCHIVE_FOLDERS_TREE_TYPE, SHARED_FOLDERS_TREE_TYPE } from 'owa-folders';
import { getGroupMailboxInfo } from 'owa-group-common';
import { isPublicFolderComposeViewState } from 'owa-mail-compose-store/lib/utils/createPublicFolderComposeViewState';
import { getSelectedNode } from 'owa-mail-folder-forest-store';
import createItem from 'owa-mail-store/lib/services/createItem';
import createResponseFromModernGroup from 'owa-mail-store/lib/services/createResponseFromModernGroupItem';
import postGroupItem from 'owa-mail-store/lib/services/postGroupItem';
import PostUnifiedGroupItem from 'owa-mail-store/lib/services/PostUnifiedGroupItem';
import sendOrSaveMessageService from 'owa-mail-store/lib/services/sendOrSaveMessageService';
import { getStore } from 'owa-mail-store/lib/store/Store';
import { getDlpInformationFromViewState } from 'owa-policy-tips/lib/utils/getDlpInformationFromViewState';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import ExtendedPropertyType from 'owa-service/lib/contract/ExtendedPropertyType';
import isSmimeEnabledInViewState from './isSmimeEnabledInViewState';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import Message from 'owa-service/lib/contract/Message';
import PostItem from 'owa-service/lib/contract/PostItem';
import PostUnifiedGroupItemResponse from 'owa-service/lib/contract/PostUnifiedGroupItemResponse';
import approveRequestItem from 'owa-service/lib/factory/approveRequestItem';
import bodyContentType from 'owa-service/lib/factory/bodyContentType';
import message from 'owa-service/lib/factory/message';
import postItem from 'owa-service/lib/factory/postItem';
import rejectRequestItem from 'owa-service/lib/factory/rejectRequestItem';
import replyAllToItem from 'owa-service/lib/factory/replyAllToItem';
import replyToItem from 'owa-service/lib/factory/replyToItem';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { isItemClassSmime } from 'owa-smime/lib/utils/smimeItemClassUtils';
import { format } from 'owa-strings';
import getAddSmimePropertiesRequestObject from './getAddSmimePropertiesRequestObject';
import { lazyTryAddSmimeProperties } from 'owa-smime';
import DocLink from 'owa-service/lib/contract/DocLink';
import { getDocLinks } from './getDocLinks';
import { CANCEL_SMIME_SEND_ERROR_MESSAGE } from 'owa-smime/lib/utils/constants';

const HTML_WRAPPER =
    '<html><head><meta http-equiv="Content-Type" content="text/html; charset=UTF-8">' +
    '<style type="text/css" style="display:none;"> P {{margin-top:0;margin-bottom:0;}} </style>' +
    '</head><body dir="{0}">{1}</body></html>';

const ITEM_CLASS_NOTE = 'IPM.Note';

export interface CustomError extends Error {
    isAsyncSend?: boolean;
    showErrorToUser?: boolean;
    messageText?: string;
}

async function loadAtMentionsData(viewState: ComposeViewState, isSend: boolean, message: Message) {
    const plugins = findComposePlugins(viewState);
    // Stamp mentions related properties here, as they differ for send vs save.
    if (plugins && plugins.mentionsPickerPlugin && plugins.mentionsPickerPlugin.isImported()) {
        const pickerPlugin = await plugins.mentionsPickerPlugin.getPlugin();
        const dataProvider = pickerPlugin.dataProvider as MentionPluginDataProviderInterface;
        if (isSend) {
            const userSession = getUserConfiguration().SessionSettings;
            const mentionWrappers = dataProvider.getMentionsWrappersForSend(<EmailAddressWrapper>{
                MailboxType: 'Mailbox',
                RoutingType: 'SMTP',
                EmailAddress: userSession.UserEmailAddress,
                Name: userSession.UserDisplayName,
            });
            if (mentionWrappers && mentionWrappers.length > 0) {
                message.MentionsEx = mentionWrappers;
            }
        } else {
            const pendingTags = dataProvider.getPendingTagsForSave();
            if (pendingTags.length > 0) {
                message.PendingSocialActivityTagIds = pendingTags;
            }
        }
    }
}

function getPostForRequest(viewState: ComposeViewState): PostItem {
    let bodyValue = viewState.content;

    if (viewState.bodyType == 'HTML') {
        const textDirection =
            viewState.explicitTextDirection ||
            (getUserConfiguration().SessionSettings.IsUserCultureRightToLeft
                ? TextDirection.RTL
                : TextDirection.LTR);
        bodyValue = removeSrcAttributeFromInlineImages(bodyValue);
        bodyValue = format(HTML_WRAPPER, textDirection, bodyValue);
    }

    const body = bodyContentType({ BodyType: viewState.bodyType, Value: bodyValue });
    body.DataUriCount = getDataUriCount(viewState.content);

    let postType: PostItem;

    const commonPostFields: Partial<PostItem> = {
        Subject: viewState.subject || '',
        Importance: viewState.importance,
        Sensitivity: viewState.sensitivity,
    };

    postType = postItem({
        ...commonPostFields,
        Body: body,
    });

    if (viewState.itemId) {
        postType.ItemId = viewState.itemId;
    }

    if (viewState.fromViewState) {
        setFromInfoToPost(viewState.fromViewState, postType);
    }
    return postType;
}

export function getMessageForRequest(viewState: ComposeViewState): Message {
    let bodyValue = viewState.content;
    const renameOriginalSrc = isSmimeEnabledInViewState(viewState);
    if (viewState.useSmartResponse) {
        bodyValue = bodyValue + createAppendOnSendBlock(viewState.bodyType);
    }
    if (viewState.bodyType == 'HTML') {
        const textDirection =
            viewState.explicitTextDirection ||
            (getUserConfiguration().SessionSettings.IsUserCultureRightToLeft
                ? TextDirection.RTL
                : TextDirection.LTR);
        bodyValue = removeSrcAttributeFromInlineImages(bodyValue, renameOriginalSrc);
        bodyValue = format(HTML_WRAPPER, textDirection, bodyValue);
    }

    const body = bodyContentType({ BodyType: viewState.bodyType, Value: bodyValue });
    body.DataUriCount = getDataUriCount(viewState.content);

    const itemId = createItemIdForRequest(viewState.itemId);
    const referenceItemId = createItemIdForRequest(viewState.referenceItemId);

    const toRecipients = getRecipientsFromWellViewState(viewState.toRecipientWell);
    const ccRecipients = getRecipientsFromWellViewState(viewState.ccRecipientWell);
    const bccRecipients = getRecipientsFromWellViewState(viewState.bccRecipientWell);

    let messageType: Message;

    const commonMessageFields: Partial<Message> = {
        Subject: viewState.subject || '',
        Importance: viewState.importance,
        IsReadReceiptRequested: viewState.isReadReceiptRequested,
        IsDeliveryReceiptRequested: viewState.isDeliveryReceiptRequested,
        ToRecipients: toRecipients,
        CcRecipients: ccRecipients,
        BccRecipients: bccRecipients,
        Sensitivity: viewState.sensitivity,
    };

    let createMessageType;
    if (viewState.useSmartResponse) {
        // If the current compose form uses smart response, we always send the CreateItem request for both send and save
        // since there is not quoted body in inline compose, we could only set NewBodyContent,
        // Server side will find out the quoate body and create this item.
        createMessageType =
            viewState.operation == ComposeOperation.ReplyAll ? replyAllToItem : replyToItem;
        messageType = createMessageType({
            ...commonMessageFields,
            UpdateResponseItemId: itemId,
            ReferenceItemId: referenceItemId,
            ReferenceItemDocumentId: viewState.referenceItemDocumentId || 0,
            NewBodyContent: body,
        });
    } else if (
        viewState.operation == ComposeOperation.Approve ||
        viewState.operation == ComposeOperation.Reject
    ) {
        createMessageType =
            viewState.operation == ComposeOperation.Approve
                ? approveRequestItem
                : rejectRequestItem;
        messageType = createMessageType({
            ...commonMessageFields,
            ReferenceItemId: referenceItemId,
            Body: body,
        });
    } else {
        // If the current compose form is full compose and the compose is newly opened, there is no itemId for it
        // We use CreateItem to create this new item.
        // When the response is back from the server, the itemId will be set.
        // Next time to send or save in full compose, we could use sendOrSaveMessageService to update item.
        messageType = message({
            ...commonMessageFields,
            Body: body,
        });

        if (viewState.itemId) {
            messageType.ItemId = viewState.itemId;
        }
    }

    if (viewState.fromViewState) {
        setFromInfoToMessage(viewState.fromViewState, messageType);
    }

    return messageType;
}

function addExtendedProperty(message: Message, extendedPropertyType: ExtendedPropertyType) {
    if (message.ExtendedProperty == null) {
        message.ExtendedProperty = [extendedPropertyType];
    } else {
        //TODO: Dedupe step.
        message.ExtendedProperty.push(extendedPropertyType);
    }
}

async function sendOrSaveMessage(
    viewState: ComposeViewState,
    isSend: boolean
): Promise<ItemInfoResponseMessage> {
    let item;
    let response;

    const timeFormat = getUserConfiguration().UserOptions.TimeFormat;

    if (isPublicFolderComposeViewState(viewState)) {
        const publicFolderComposeViewState: PublicFolderComposeViewState = viewState as PublicFolderComposeViewState;
        item = getPostForRequest(viewState);
        response = await createItem(
            item,
            viewState.bodyType,
            isSend,
            timeFormat,
            viewState.isInlineCompose,
            viewState.IRMData,
            publicFolderComposeViewState.publicFolderId
        );
    } else {
        item = getMessageForRequest(viewState);

        // Prepare any extra data prior to any send or save
        tryUpdateExtendedPropertiesForInternetHeaders(viewState, item as Message);
        tryUpdateExtendedPropertiesForAppendOnSend(viewState, item as Message, isSend);
        await loadAtMentionsData(viewState, isSend, item);

        // Figure out which API to use
        if (isSmimeEnabledInViewState(viewState)) {
            try {
                const addSmimePropertiesRequest = getAddSmimePropertiesRequestObject(
                    viewState,
                    item,
                    isSend
                );
                await lazyTryAddSmimeProperties
                    .import()
                    .then(tryAddSmimeProperties =>
                        tryAddSmimeProperties(addSmimePropertiesRequest)
                    );
            } catch (e) {
                const error = new Error() as CustomError;
                error.message =
                    e.message == CANCEL_SMIME_SEND_ERROR_MESSAGE
                        ? CANCEL_SMIME_SEND_ERROR_MESSAGE
                        : 'SmimeEncodeError';
                return Promise.reject(error);
            }
        } else if (isFeatureEnabled('cmp-smime') && viewState.itemId) {
            // Reset S/MIME item class if user switches from S/MIME to non-S/MIME scenario
            const mailItem = getStore().items.get(viewState.itemId.Id);
            if (mailItem && isItemClassSmime(mailItem.ItemClass)) {
                item.ItemClass = ITEM_CLASS_NOTE;
            }
        }

        // DLP information is only set when the message is sent.
        if (isSend) {
            const dlpProperties = getDlpInformationFromViewState(viewState.policyTipsViewState);
            dlpProperties.map(property => addExtendedProperty(item, property));
        }

        response = await selectAndExecuteSendOrSaveApi(viewState, item, timeFormat, isSend);
    }
    clearKeysOfInternetHeadersToBeRemoved(viewState);
    const responseMessage =
        response && response.ResponseMessages && response.ResponseMessages.Items
            ? (response.ResponseMessages.Items[0] as ItemInfoResponseMessage)
            : response && !response.ResponseMessages //this is needed for PostUnifiedGroupItem, which only returns a response instead of an array.
            ? (response as ItemInfoResponseMessage)
            : null;

    if (responseMessage && responseMessage.ResponseClass == 'Success') {
        return Promise.resolve(responseMessage);
    } else {
        preHandleErrorResponse(response, viewState);
        let error: CustomError;
        if (responseMessage) {
            error = new Error(responseMessage.ResponseCode) as CustomError;
            error.messageText = responseMessage.MessageText;
        } else {
            error = new Error('Unrecognized response') as CustomError;
        }
        return Promise.reject(error);
    }
}

async function selectAndExecuteSendOrSaveApi(
    viewState: ComposeViewState,
    message: Message,
    timeFormat: string,
    isSend: boolean
) {
    if (isGroupScenario(viewState)) {
        if (isSend && viewState.operation != ComposeOperation.Forward) {
            if (isConsumer() || viewState.itemId || message.RightsManagementLicenseData) {
                return await postGroupItem(
                    (<GroupComposeViewState>viewState).groupId,
                    message,
                    viewState.bodyType,
                    timeFormat,
                    viewState.isInlineCompose
                );
            }
            const groupMailboxInfo = getGroupMailboxInfo(
                (<GroupComposeViewState>viewState).groupId
            );
            return await PostUnifiedGroupItem(
                (<GroupComposeViewState>viewState).groupId,
                message,
                viewState.bodyType,
                timeFormat,
                groupMailboxInfo.requestOptions,
                viewState.isInlineCompose
            );
        } else {
            const isReply =
                viewState.operation == ComposeOperation.Reply ||
                viewState.operation == ComposeOperation.ReplyAll;

            if ((!viewState.itemId && isReply) || viewState.useSmartResponse) {
                return await createResponseFromModernGroup(
                    (<GroupComposeViewState>viewState).groupId,
                    message,
                    viewState.bodyType,
                    false /* isSend */,
                    timeFormat,
                    viewState.isInlineCompose
                );
            }
        }
    }
    const selectedNode = getSelectedNode();
    // To fix the XRF access issue, we set RemoteExecute for archive and shared folder scenarios.
    // For CreateItem, we do not set X-AnchorMailbox header, the request will always hit primary mailbox user's mailbox server.
    // On server side code, we have code fix to check the flag when reply/replyall/forward emails.
    const remoteExecute = !!(
        selectedNode &&
        (selectedNode.treeType === SHARED_FOLDERS_TREE_TYPE ||
            selectedNode.treeType === ARCHIVE_FOLDERS_TREE_TYPE)
    );

    // Set the DocLinks property for OneDrive links in the body
    const docLinks: DocLink[] = isSend ? getDocLinks(viewState) : null;

    return viewState.useSmartResponse || !viewState.itemId
        ? await createItem(
              message,
              viewState.bodyType,
              isSend,
              timeFormat,
              viewState.isInlineCompose,
              viewState.IRMData,
              null,
              remoteExecute
          )
        : await sendOrSaveMessageService(
              message,
              isSend,
              viewState.IRMData,
              viewState.keysOfInternetHeadersToBeRemoved,
              docLinks
          );
}

export function sendMessage(viewState: ComposeViewState): Promise<ItemInfoResponseMessage> {
    return sendOrSaveMessage(viewState, true /*isSend*/);
}

export function saveMessage(viewState: ComposeViewState): Promise<ItemInfoResponseMessage> {
    return sendOrSaveMessage(viewState, false /*isSend*/);
}

export function handleSuccessResponse(
    responseMessage: ItemInfoResponseMessage,
    viewState: ComposeViewState
): boolean {
    const message = getMessageFromItemInfoResponseMessage(responseMessage);
    if (message) {
        if (message.ItemId) {
            updateComposeItemId(
                viewState,
                message.ItemId.Id,
                message.ItemId.ChangeKey,
                message.LastModifiedTime
            );

            if (message.Body) {
                viewState.lastSavedMessageBody = message.Body.Value;
            }
        }
        if (message.InternetMessageId) {
            updateInternetMessageId(
                viewState.conversationId,
                viewState.itemId ? viewState.itemId.Id : null,
                message.InternetMessageId
            );
        }
        return true;
    }
    return false;
}

function getMessageFromItemInfoResponseMessage(responseMessage: ItemInfoResponseMessage): Message {
    // VSO 16297: responseMessage.Items is null when the request contains attachment
    // Workaround it here to check null before we use it. We can remove this check or this comment once we figure out why Items can be null
    if (responseMessage && responseMessage.Items) {
        return responseMessage.Items[0] as Message;
    } else if (
        responseMessage &&
        ((responseMessage as object) as PostUnifiedGroupItemResponse).Item
    ) {
        return ((responseMessage as object) as PostUnifiedGroupItemResponse).Item as Message;
    }

    return null;
}
